/**
 * EngagementClosingFxRatesSection — Phase 6e.4.1 rewrite.
 *
 * Renders the engagement-level closing-FX-rates section inside an
 * `Accordion` that auto-populates from TCMB's daily XML feed.
 *
 * Audit framing (IAS 21 §23(a) + §28):
 *   • The closing rate is the TCMB Döviz Alış rate on the audit
 *     period end date — the same source the tax authority uses, so
 *     it's the strongest evidence trail Turkish auditors can
 *     produce.
 *   • The auditor doesn't always know which currencies they'll
 *     need before contracts are added. The "🔄 TCMB'den Tümünü
 *     Getir" button pulls EVERY currency TCMB publishes (~23) for
 *     `auditPeriodEnd`, so a later contract in CHF / SEK / CAD has
 *     a rate on file without a redrive.
 *   • Auditor overrides are first-class — editing an auto-filled
 *     value flips its badge from `TCMB` to `Manuel` so the
 *     reviewer can spot every deviation from source.
 *   • Weekend / holiday end dates → the button falls back up to 14
 *     calendar days back and renders an amber `*` + tooltip on the
 *     source caption.
 *
 * Pre-6e.4.1 the section seeded fixed USD/EUR/GBP rows with no
 * backend coupling. That pattern is gone — the TCMB fetch replaces
 * it. The accordion starts COLLAPSED when no closing rates are set
 * and OPEN when at least one rate is present, so reload preserves
 * the auditor's visual context.
 */

import { AlertTriangle, RefreshCw } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";
import { toast } from "sonner";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Admonition } from "@/components/ui/admonition";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FormMessageT } from "@/features/contract-form/components/FormMessageT";
import { fetchFxRates } from "@/features/fx-sync/fxRatesClient";
import { getCurrentLocale } from "@/lib/format";
import { sanitizeFxRatePaste } from "@/lib/format/sanitizeFxRatePaste";
import { cn } from "@/lib/utils";
import type { EngagementCreationFormValues } from "@/schemas/engagement-creation";
import type { ContractInput } from "@/types";

import { deriveFxCurrencies } from "../fx-derivation";

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const WEEKEND_FALLBACK_WINDOW_DAYS = 14;

/**
 * Phase 6e.4.2 — pin USD/EUR/GBP to the top of the closing-rates
 * list. The remaining currencies stay alphabetical. A dashed
 * separator marks the boundary, but ONLY when both groups are
 * non-empty (so a USD-only or JPY-only engagement renders cleanly).
 */
const ANCHOR_CURRENCIES = ["USD", "EUR", "GBP"] as const;

function sortClosingRateCurrencies(currencies: string[]): string[] {
  const set = new Set(currencies);
  const anchors = ANCHOR_CURRENCIES.filter((c) => set.has(c));
  const rest = currencies
    .filter(
      (c) => !(ANCHOR_CURRENCIES as readonly string[]).includes(c),
    )
    .sort();
  return [...anchors, ...rest];
}

export interface EngagementClosingFxRatesSectionProps {
  /**
   * Live contract list for the engagement under edit. In create-mode
   * this is an empty array — the section then renders the
   * "create-mode" empty admonition that points the auditor at the
   * post-create flow.
   */
  contracts: ReadonlyArray<ContractInput | null>;
  /** ISO YYYY-MM-DD audit period end, used in the helper text label. */
  auditPeriodEnd?: string;
  /**
   * Distinguishes create-mode (empty engagement, encourages the
   * auditor to return later) from edit-mode (existing engagement, may
   * legitimately have zero FX contracts).
   */
  mode: "create" | "edit";
}

interface FetchMeta {
  source: string;
  fetchedAt: string;
  ratesDate: string;
  fallbackUsed: boolean;
}

export function EngagementClosingFxRatesSection({
  contracts,
  auditPeriodEnd,
  mode,
}: EngagementClosingFxRatesSectionProps) {
  const { t } = useTranslation();
  const form = useFormContext<EngagementCreationFormValues>();
  const functionalCurrency = form.watch("functionalCurrency");

  const derivedCurrencies = deriveFxCurrencies(
    contracts as Array<ContractInput | null>,
    functionalCurrency,
  );

  // Reactive read of closingFxRates so the UI re-renders when the
  // TCMB fetch writes back via `form.setValue`, when the auditor
  // types into a cell, or when the page hydrates an existing
  // engagement.
  const closingFxRatesValue = useWatch({
    control: form.control,
    name: "closingFxRates",
  });
  const enteredCurrencies = useMemo(
    () => Object.keys(closingFxRatesValue ?? {}),
    [closingFxRatesValue],
  );

  // Track the rates the TCMB fetch wrote, so we can render the
  // `TCMB` badge while the current cell value matches and flip to
  // `Manuel` once the auditor edits. Component-local state — the
  // engagement object never carries this distinction.
  const [originalTcmbRates, setOriginalTcmbRates] = useState<
    Record<string, string>
  >({});
  const [fetchMeta, setFetchMeta] = useState<FetchMeta | null>(null);
  const [loading, setLoading] = useState(false);

  // Accordion starts COLLAPSED when nothing has been set; OPEN once
  // any closing rate is present (either fetched or hydrated from
  // localStorage). useWatch is async w.r.t. the initial render, so
  // we read the synchronous getValues() snapshot once at mount.
  const [open, setOpen] = useState<string>(() => {
    const initial = form.getValues("closingFxRates") ?? {};
    return Object.keys(initial).length > 0 ? "closing-rates" : "";
  });

  // ── Visible rows ─────────────────────────────────────────────────────
  // Derived contracts' currencies that are NOT yet in the form
  // state → flagged in a destructive admonition (closing rate is
  // mandatory for those currencies). The visible row list still
  // includes them so the auditor can fill them in.
  const visibleCurrencies = useMemo(() => {
    const set = new Set<string>([
      ...derivedCurrencies,
      ...enteredCurrencies.filter((c) => c !== functionalCurrency),
    ]);
    return sortClosingRateCurrencies(Array.from(set));
  }, [derivedCurrencies, enteredCurrencies, functionalCurrency]);

  // Phase 6e.4.2 — split index for the dashed separator between
  // pinned (USD/EUR/GBP) and the rest. The separator renders only
  // when both groups are non-empty.
  const anchorBoundary = visibleCurrencies.findIndex(
    (c) => !(ANCHOR_CURRENCIES as readonly string[]).includes(c),
  );
  const anchorsPresent = anchorBoundary > 0 || anchorBoundary === -1
    ? visibleCurrencies.some((c) =>
        (ANCHOR_CURRENCIES as readonly string[]).includes(c),
      )
    : false;
  const nonAnchorsPresent = anchorBoundary >= 0;
  const showAnchorSeparator = anchorsPresent && nonAnchorsPresent;

  // Phase 6e.4.2 — trigger summary reads "X/Y döviz dolu" instead
  // of just "N". `filled` = visible rows with a non-empty rate.
  const filledCount = visibleCurrencies.reduce((n, ccy) => {
    const v = (closingFxRatesValue as Record<string, string> | undefined)?.[
      ccy
    ];
    return typeof v === "string" && v.length > 0 ? n + 1 : n;
  }, 0);

  const missingForDerived = derivedCurrencies.filter(
    (ccy) =>
      !closingFxRatesValue ||
      typeof (closingFxRatesValue as Record<string, string>)[ccy] !==
        "string" ||
      ((closingFxRatesValue as Record<string, string>)[ccy] ?? "").length === 0,
  );

  // ── TCMB fetch handler ───────────────────────────────────────────────
  const canFetch =
    typeof auditPeriodEnd === "string" && ISO_DATE_RE.test(auditPeriodEnd);

  async function handleFetchTcmb() {
    if (!canFetch || !auditPeriodEnd) return;
    setLoading(true);
    try {
      // Phase 6e.4.4 — pass functional currency to the backend so it
      // applies IAS 21 §B6 cross-rates (X/F = (X/TRY) / (F/TRY))
      // before responding. For TRY-functional the backend skips the
      // math entirely and returns raw X/TRY. The frontend does NO FX
      // math after this commit — backend is the single source of truth.
      const fcArg =
        typeof functionalCurrency === "string" &&
        functionalCurrency.trim().length > 0
          ? { functionalCurrency }
          : {};

      // 1) Try the audit-period-end day alone.
      let response = await fetchFxRates({
        startDate: auditPeriodEnd,
        endDate: auditPeriodEnd,
        ...fcArg,
      });
      let row = response.data.rows[0];
      let ratesDate = row?.date ?? auditPeriodEnd;
      let fallbackUsed = false;

      // 2) Weekend / holiday → walk back up to 14 days.
      if (!row && response.data.missingDates.length > 0) {
        const fallbackStart = subtractCalendarDays(
          auditPeriodEnd,
          WEEKEND_FALLBACK_WINDOW_DAYS,
        );
        response = await fetchFxRates({
          startDate: fallbackStart,
          endDate: auditPeriodEnd,
          ...fcArg,
        });
        if (response.data.rows.length > 0) {
          row = response.data.rows[response.data.rows.length - 1];
          ratesDate = row?.date ?? auditPeriodEnd;
          fallbackUsed = true;
        }
      }

      if (!row) {
        toast.error(
          t("engagement.fx.tcmb.fetchAllError", {
            message: t("engagement.fx.tcmb.fetchAllDisabledTooltip"),
          }),
        );
        return;
      }

      const rates = row.rates;
      const next: Record<string, string> = {
        ...(form.getValues("closingFxRates") ?? {}),
      };
      const tcmbSnapshot: Record<string, string> = {};
      let written = 0;
      for (const [ccy, value] of Object.entries(rates)) {
        if (value === null || !Number.isFinite(value)) continue;
        // Defensive: the backend already omits the functional currency
        // from the cross-rate response. This skip catches the
        // TRY-functional path (raw fetch returns no TRY key but, if a
        // future backend change started including F=TRY, we wouldn't
        // re-derive functional/functional = 1.0 into the form).
        if (ccy === functionalCurrency) continue;
        // Phase 6e.4.4 — dynamic decimal precision: rates ≥ 0.1 use 4
        // decimals (the legacy TCMB display); sub-0.1 values get 6
        // decimals so cross-rates like TRY/USD ≈ 0.028345 don't read
        // 0.0283 and lose the auditable last two digits.
        const asString = formatRate(value);
        next[ccy] = asString;
        tcmbSnapshot[ccy] = asString;
        written += 1;
      }
      form.setValue(
        "closingFxRates",
        next as EngagementCreationFormValues["closingFxRates"],
        { shouldDirty: true, shouldValidate: false },
      );
      setOriginalTcmbRates(tcmbSnapshot);
      setFetchMeta({
        source: response.data.source,
        fetchedAt: response.data.fetchedAt,
        ratesDate,
        fallbackUsed,
      });
      setOpen("closing-rates");
      toast.success(
        t("engagement.fx.tcmb.fetchAllSuccess", {
          count: written,
          date: ratesDate,
        }),
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : String(err ?? "unknown");
      toast.error(
        t("engagement.fx.tcmb.fetchAllError", { message }),
      );
    } finally {
      setLoading(false);
    }
  }

  function resetRow(ccy: string) {
    // Clear via the per-path setValue so the child FormField's
    // `field.value` updates immediately. A parent-path setValue
    // with the key removed via destructuring doesn't always
    // propagate to a registered nested FormField in RHF v7 — the
    // direct-path overwrite is unambiguous.
    form.setValue(
      `closingFxRates.${ccy}` as never,
      "" as never,
      { shouldDirty: true, shouldValidate: false },
    );
    // Then also drop the key from the parent record so a freshly
    // submitted form doesn't carry an empty-string rate. Pre-6e.4.1
    // the schema would have rejected that anyway; post-6e.4.1 we
    // still want clean form state.
    const current = form.getValues("closingFxRates") ?? {};
    if (ccy in current) {
      const { [ccy]: _removed, ...rest } = current as Record<string, string>;
      form.setValue(
        "closingFxRates",
        rest as EngagementCreationFormValues["closingFxRates"],
        { shouldDirty: true, shouldValidate: false },
      );
    }
    setOriginalTcmbRates((prev) => {
      const next = { ...prev };
      delete next[ccy];
      return next;
    });
  }

  // ── Render ───────────────────────────────────────────────────────────
  const summary =
    visibleCurrencies.length > 0 && auditPeriodEnd
      ? t("engagement.fx.closing.accordionSummary", {
          filled: filledCount,
          total: visibleCurrencies.length,
          date: formatTrDate(auditPeriodEnd) ?? auditPeriodEnd,
        })
      : t("engagement.fx.closing.accordionSummaryEmpty");

  return (
    <TooltipProvider>
      <section
        className="space-y-3 rounded-lg border border-dashed p-4"
        data-testid="engagement-fx-closing-section"
      >
        <Accordion
          type="single"
          collapsible
          value={open}
          onValueChange={setOpen}
        >
          <AccordionItem value="closing-rates" className="border-0">
            <div className="flex items-center gap-2">
              <AccordionTrigger
                className="flex-1"
                data-testid="engagement-fx-closing-trigger"
              >
                <span className="text-sm font-medium">{summary}</span>
              </AccordionTrigger>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={canFetch ? undefined : 0}>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!canFetch || loading}
                      aria-busy={loading}
                      onClick={handleFetchTcmb}
                      data-testid="engagement-fx-tcmb-fetch-all"
                    >
                      <RefreshCw
                        className={cn(
                          "mr-1 h-4 w-4",
                          loading && "animate-spin",
                        )}
                        aria-hidden="true"
                      />
                      {loading
                        ? t("engagement.fx.tcmb.fetchAllLoading")
                        : t("engagement.fx.tcmb.fetchAll")}
                    </Button>
                  </span>
                </TooltipTrigger>
                {!canFetch && (
                  <TooltipContent>
                    {t("engagement.fx.tcmb.fetchAllDisabledTooltip")}
                  </TooltipContent>
                )}
              </Tooltip>
            </div>

            <AccordionContent>
              <div className="space-y-3 pt-2">
                {/* Phase 6e.4.4 — provenance note. TRY-functional reads
                    the raw TCMB rates; everything else relies on IAS 21
                    §B6 cross-rate substitution that the backend has
                    already applied. Surface the source so the auditor
                    can defend the figures. */}
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="engagement-fx-closing-provenance"
                >
                  {functionalCurrency === "TRY"
                    ? t("engagement.closingFxRates.tcmbProvenance")
                    : t(
                        "engagement.closingFxRates.crossRateProvenance",
                        { functional: functionalCurrency },
                      )}
                </p>

                {fetchMeta && (
                  <p
                    className="text-xs text-muted-foreground"
                    data-testid="engagement-fx-tcmb-meta"
                  >
                    {t("engagement.fx.closing.metaSource", {
                      source: fetchMeta.source,
                      ratesDate: fetchMeta.ratesDate,
                      timestamp: fetchMeta.fetchedAt,
                    })}
                    {fetchMeta.fallbackUsed && (
                      <span
                        className="ml-1 text-amber-600 dark:text-amber-400"
                        title={t("engagement.fx.closing.metaFallback")}
                        data-testid="engagement-fx-tcmb-meta-fallback"
                      >
                        *
                      </span>
                    )}
                  </p>
                )}

                {missingForDerived.length > 0 && (
                  <Admonition type="danger">
                    <span className="flex items-start gap-2">
                      <AlertTriangle
                        className="h-4 w-4 mt-0.5 shrink-0"
                        aria-hidden="true"
                      />
                      <span data-testid="engagement-fx-closing-missing-rates">
                        {t(
                          "engagement.fx.closing.missingRatesAdmonition",
                          { currencies: missingForDerived.join(", ") },
                        )}
                      </span>
                    </span>
                  </Admonition>
                )}

                {visibleCurrencies.length === 0 ? (
                  <Admonition type="info">
                    {mode === "create"
                      ? t("engagement.fx.closing.noFxContractsCreateMode")
                      : t("engagement.fx.closing.noFxContracts")}
                  </Admonition>
                ) : (
                  <div className="space-y-3">
                    {visibleCurrencies.map((ccy, idx) => {
                      const isFirstNonAnchor =
                        showAnchorSeparator && idx === anchorBoundary;
                      const currentValue =
                        (closingFxRatesValue as
                          | Record<string, string>
                          | undefined)?.[ccy] ?? "";
                      const originalValue = originalTcmbRates[ccy];
                      const cameFromTcmb = originalValue !== undefined;
                      const isUntouched =
                        cameFromTcmb && currentValue === originalValue;
                      return (
                        <Fragment key={ccy}>
                          {isFirstNonAnchor && (
                            <div
                              className="border-t border-dashed my-2"
                              data-testid="engagement-fx-closing-anchor-separator"
                            />
                          )}
                          <FormField
                          control={form.control}
                          name={`closingFxRates.${ccy}` as never}
                          render={({ field }) => (
                            <FormItem
                              data-testid={`engagement-fx-closing-row-${ccy}`}
                            >
                              <FormLabel>
                                {t("engagement.fx.closing.rateLabel", {
                                  currency: ccy,
                                  functional: functionalCurrency,
                                })}
                                <span className="text-destructive">*</span>
                              </FormLabel>
                              <div className="flex items-center gap-2">
                                <FormControl>
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={t(
                                      "engagement.fx.closing.ratePlaceholder",
                                    )}
                                    className="font-mono"
                                    {...field}
                                    value={
                                      (field.value as
                                        | string
                                        | undefined) ?? ""
                                    }
                                    // SPEC-08 §6 — sanitize pasted values
                                    // (strip non-numeric trailing chars,
                                    // normalise decimal separator) before
                                    // they hit RHF + Zod. Falls back to the
                                    // raw paste if the result isn't a
                                    // plausible number so genuine bad input
                                    // still surfaces the validation error.
                                    onPaste={(e) => {
                                      const raw = e.clipboardData.getData("text");
                                      const cleaned = sanitizeFxRatePaste(raw, getCurrentLocale());
                                      if (cleaned !== raw) {
                                        e.preventDefault();
                                        field.onChange(cleaned);
                                      }
                                    }}
                                    data-testid={`engagement-fx-closing-rate-${ccy}`}
                                  />
                                </FormControl>
                                {cameFromTcmb &&
                                  (isUntouched ? (
                                    <span
                                      className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                                      data-testid={`engagement-fx-tcmb-badge-${ccy}`}
                                    >
                                      {t("engagement.fx.closing.tcmbBadge")}
                                    </span>
                                  ) : (
                                    <span
                                      className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                                      data-testid={`engagement-fx-manual-badge-${ccy}`}
                                    >
                                      {t("engagement.fx.closing.manualBadge")}
                                    </span>
                                  ))}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => resetRow(ccy)}
                                  aria-label={t(
                                    "engagement.fx.closing.resetRowAria",
                                    { currency: ccy },
                                  )}
                                  data-testid={`engagement-fx-closing-reset-${ccy}`}
                                >
                                  {t("engagement.fx.closing.resetRow")}
                                </Button>
                              </div>
                              <FormMessageT />
                            </FormItem>
                          )}
                        />
                        </Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>
    </TooltipProvider>
  );
}

function subtractCalendarDays(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function formatTrDate(iso: string | undefined | null): string | null {
  if (!iso || !ISO_DATE_RE.test(iso)) return null;
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

/**
 * Phase 6e.4.4 — dynamic decimal precision for the rate cell display.
 *
 * Rates ≥ 0.1 get 4 decimals (legacy TCMB display — `35.2803`).
 * Sub-0.1 values get 6 decimals so cross-rates like `TRY/USD ≈
 * 0.028345` don't read `0.0283` and lose the auditable last two
 * digits. Sub-0.001 values get 8 decimals as a defensive floor.
 */
export function formatRate(value: number | string): string {
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n) || n === 0) return String(value);
  const abs = Math.abs(n);
  if (abs >= 0.1) return n.toFixed(4);
  if (abs >= 0.001) return n.toFixed(6);
  return n.toFixed(8);
}
